import React from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Conversation } from '../../../types/chat';

interface ConversationSidebarProps {
  conversations: Conversation[];
  selectedConversationId: string | null;
  conversationsLoading: boolean;
  conversationLoading: boolean;
  handleSelectConversation: (id: string) => void;
  handleCreateNewConversation: () => void;
  isMobile: boolean;
  onClose?: () => void;
}

const ConversationSkeleton: React.FC = () => (
  <div className="animate-pulse flex space-x-4 p-3">
    <div className="flex-1 space-y-2 py-1">
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
    </div>
    <div className="h-4 w-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
  </div>
);

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  selectedConversationId,
  conversationsLoading,
  conversationLoading,
  handleSelectConversation,
  handleCreateNewConversation,
  isMobile,
  onClose
}) => {
  const { t } = useTranslation(['v0/chat', 'v0/common']);

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 flex items-center justify-between flex-shrink-0">
        <button
          type="button"
          onClick={handleCreateNewConversation}
          disabled={conversationsLoading || conversationLoading}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed w-full"
        >
          <FiPlus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          {t('v0/chat:newConversation')}
        </button>
        {isMobile && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-3 p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500"
            aria-label={t('v0/common:close')}
          >
            <FiX className="h-6 w-6" aria-hidden="true" />
          </button>
        )}
      </div>
      <hr className="border-t border-border-light dark:border-border-dark mx-4" />
      <div className="flex-grow overflow-y-auto p-4 space-y-2">
        {conversationsLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <ConversationSkeleton key={i} />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-center text-text-secondary dark:text-text-secondary-dark px-4">
            <p>{t('v0/chat:noConversations')}</p>
            <p className="text-sm">{t('v0/chat:clickNewConversation')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {conversations.map((conversation) => {
              const isSelected = selectedConversationId === conversation.id;
              return (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => handleSelectConversation(conversation.id)}
                  className={`w-full text-left p-3 rounded-md transition-colors duration-150 ease-in-out ${
                    isSelected
                      ? 'bg-gray-100 dark:bg-gray-700'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1 overflow-hidden mr-2">
                      <p className={`text-sm font-medium truncate ${
                          isSelected
                            ? 'text-gray-900 dark:text-white'
                            : 'text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {conversation.title || t('v0/chat:newConversation')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {format(new Date(conversation.updatedAt), 'yyyy/MM/dd HH:mm')}
                      </p>
                    </div>
                    <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                        isSelected
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-100'
                      }`}
                    >
                      {t('v0/chat:messageCount_plural', { count: conversation.messageCount || 0 })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};